import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeader from "@/components/base/SectionHeader";
import { comments as mockComments, type Comment } from "@/mocks/melons";

const STANCE_STYLE: Record<string, string> = {
  理性吃瓜: "bg-primary-100 text-primary-800",
  蹲反转: "bg-secondary-100 text-secondary-800",
  支持一方: "bg-accent-100 text-accent-800",
  纯看热闹: "bg-background-100 text-foreground-600",
  提出质疑: "bg-secondary-200 text-secondary-900",
};

function Avatar({ name, seed }: { name: string; seed: string }) {
  const palette = ["bg-primary-500", "bg-accent-500", "bg-secondary-600", "bg-primary-700", "bg-accent-700", "bg-secondary-700"];
  const tone = palette[name.charCodeAt(0) % palette.length];
  return (
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone} text-sm font-medium text-background-50`}>
      {seed}
    </span>
  );
}

export default function TalkSection() {
  const [items, setItems] = useState<Comment[]>(mockComments);
  const [nickname, setNickname] = useState("");
  const [text, setText] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const content = text.trim();
    if (!content) return;
    const name = nickname.trim() || "匿名吃瓜群众";
    const newComment: Comment = {
      id: Date.now(),
      name,
      avatar: name.slice(0, 1).toUpperCase() + name.slice(-1).toUpperCase(),
      stance: "理性吃瓜",
      content,
      likes: "0",
      agree: 100,
    };
    setItems((prev) => [newComment, ...prev]);
    setNickname("");
    setText("");
  }

  return (
    <section id="talk">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          eyebrow="网友热议"
          eyebrowIcon="ri-chat-3-fill"
          title="评论区里的真实声音"
          desc="每个人立场不同，围观方式也不同。欢迎你在下方留言，加入这场讨论；评论仅代表发言网友个人观点。"
        />

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <Reveal>
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-background-200 bg-background-50 p-4 shadow-soft md:p-5"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                  <i className="ri-chat-new-line text-primary-600" /> 来，说两句你的看法
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-[200px_1fr]">
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="你的昵称（可留空）"
                    maxLength={20}
                    className="w-full rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                  />
                  <div className="relative">
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="围绕大瓜讨论区话题，理性发言，不造谣不传谣……"
                      rows={3}
                      maxLength={200}
                      className="w-full resize-none rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    />
                    <span className="pointer-events-none absolute bottom-2.5 right-3 text-xs text-foreground-400">{text.length}/200</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-col justify-end gap-2 sm:flex-row sm:items-center">
                  <p className="flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-shield-check-line text-primary-600" /> 请勿传播未经核实的信息
                  </p>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-send-plane-line" /> 发布留言
                  </button>
                </div>
              </form>
            </Reveal>

            <div className="mt-5 flex flex-col gap-4">
              {items.map((c) => (
                <Reveal key={c.id}>
                  <article className="rounded-2xl border border-background-200 bg-background-50 p-4 shadow-soft md:p-5">
                    <div className="flex items-center gap-3">
                      <Avatar name={c.name} seed={c.avatar} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground-900">{c.name}</p>
                        <span className={`mt-0.5 inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${STANCE_STYLE[c.stance] ?? STANCE_STYLE["理性吃瓜"]}`}>
                          {c.stance}
                        </span>
                      </div>
                      <span className="ml-auto shrink-0 text-xs text-foreground-500">
                        {c.likes === "0" ? "刚刚" : `获赞 ${c.likes}`}
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-800">{c.content}</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-600">
                      <span className="flex items-center gap-1">
                        认同度
                        <span className="flex h-1.5 w-20 items-center overflow-hidden rounded-full bg-background-100">
                          <span className="h-full rounded-full bg-primary-500" style={{ width: `${c.agree}%` }} />
                        </span>
                        <span className="font-medium text-foreground-800">{c.agree}%</span>
                      </span>
                      <button type="button" className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-200 px-2.5 py-1 text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600">
                        <i className="ri-thumb-up-line" /> 认同 {c.likes === "0" ? "" : c.likes}
                      </button>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={120}>
            <aside className="flex flex-col gap-5">
              <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-book-open-line text-accent-600" /> 理性吃瓜公约
                </p>
                <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                  <li className="flex gap-2.5"><i className="ri-checkbox-circle-line text-primary-600" /> 先求证、再表达，看到反转及时更新观点。</li>
                  <li className="flex gap-2.5"><i className="ri-checkbox-circle-line text-primary-600" /> 就事论事，不对当事人进行人身攻击与造谣。</li>
                  <li className="flex gap-2.5"><i className="ri-checkbox-circle-line text-primary-600" /> 区分事实与观点，标注「来源」「进展」「网友观点」。</li>
                  <li className="flex gap-2.5"><i className="ri-checkbox-circle-line text-primary-600" /> 尊重隐私，不传播未经许可的个人信息。</li>
                </ul>
              </div>

              <div className="overflow-hidden rounded-2xl bg-accent-500 p-5 text-background-50">
                <p className="font-heading text-xl leading-tight">本期高赞金句</p>
                <blockquote className="mt-3 border-l-2 border-background-50/70 pl-3 text-sm leading-relaxed text-accent-50">
                  “热度会骗人，但把脉络理清的时间不会白费。”
                </blockquote>
                <p className="mt-3 text-xs text-accent-50/80">—— 来自网友「一颗甜梨」，获 1.5k 认同</p>
              </div>

              <div className="rounded-2xl border border-background-200 bg-background-50 p-5 text-sm text-foreground-700 shadow-soft">
                <p className="flex items-center gap-2 font-semibold text-foreground-900">
                  <i className="ri-lightbulb-flash-line text-secondary-600" /> 你有独家线索？
                </p>
                <p className="mt-2 leading-relaxed">看到值得围观的大瓜线索？整理后通过页尾投稿表单发给我们，编辑部会评估是否做成专题。</p>
                <a href="#submit" className="mt-3 inline-flex items-center gap-1 whitespace-nowrap font-medium text-primary-700 hover:text-primary-800">
                  去投稿线索 <i className="ri-arrow-right-line" />
                </a>
              </div>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}